// Utility for handling flexible tip targeting rules
// Supports targeting by user IDs, roles, parts, or "everyone"

export class TipTargeting {
  /**
   * Available user roles for targeting
   */
  static AVAILABLE_ROLES = ['user', 'admin', 'superAdmin'];

  /**
   * Check if a tip should be visible to a specific user
   * @param {Object} tip - The tip object with target_user_ids and target_rules
   * @param {Object} user - The current user object
   * @param {Object} context - Additional context (e.g., current part, score info)
   * @returns {boolean} - Whether the tip should be visible to this user
   */
  static shouldShowTipToUser(tip, user, context = {}) {
    if (!tip || !user) return false;

    // console.log('TipTargeting: Evaluating tip for user', {
    //   tipId: tip.id,
    //   tipContent: tip.content?.substring(0, 50) + '...',
    //   userId: user.id,
    //   creatorUserId: tip.creator_user_id,
    //   targetUserIds: tip.target_user_ids,
    //   targetRules: tip.target_rules
    // });

    // Always show tips created by the user themselves
    if (tip.creator_user_id === user.id) {
      // console.log('TipTargeting: Showing tip because user is creator');
      return true;
    }

    // Check specific user targeting
    if (tip.target_user_ids && tip.target_user_ids.includes(user.id)) {
      // console.log('TipTargeting: Showing tip because user is in target_user_ids');
      return true;
    }

    // Check flexible targeting rules
    if (tip.target_rules && Object.keys(tip.target_rules).length > 0) {
      const rulesResult = this.evaluateTargetingRules(tip.target_rules, user, context);
      // console.log('TipTargeting: Rules evaluation result:', rulesResult);
      return rulesResult;
    }

    // console.log('TipTargeting: Not showing tip - no matching criteria');
    return false;
  }

  /**
   * Evaluate flexible targeting rules
   * @param {Object} rules - The targeting rules object
   * @param {Object} user - The current user object
   * @param {Object} context - Additional context
   * @returns {boolean} - Whether the rules match for this user
   */
  static evaluateTargetingRules(rules, user, context) {
    // Rule: everyone
    if (rules.everyone === true) {
      return true;
    }

    // Rule: specific roles
    if (rules.roles && Array.isArray(rules.roles)) {
      if (user.role && rules.roles.includes(user.role)) {
        return true;
      }
    }

    // Rule: specific permissions
    if (rules.permissions && Array.isArray(rules.permissions)) {
      if (user.permissions && rules.permissions.some(perm => user.permissions.includes(perm))) {
        return true;
      }
    }

    // Rule: specific parts (for future implementation)
    if (rules.parts && Array.isArray(rules.parts) && context.currentPart) {
      if (rules.parts.includes(context.currentPart)) {
        return true;
      }
    }

    // Rule: exclude specific users
    if (rules.exclude_users && Array.isArray(rules.exclude_users)) {
      if (rules.exclude_users.includes(user.id)) {
        return false;
      }
    }

    return false;
  }

  /**
   * Create targeting rules for common scenarios
   */
  static createTargetingRules = {
    // Target everyone
    everyone: () => ({ everyone: true }),

    // Target specific roles
    roles: (roleList) => ({ roles: Array.isArray(roleList) ? roleList : [roleList] }),

    // Target users with specific permissions
    permissions: (permissionList) => ({ 
      permissions: Array.isArray(permissionList) ? permissionList : [permissionList] 
    }),

    // Target specific parts (for future use)
    parts: (partList) => ({ parts: Array.isArray(partList) ? partList : [partList] }),

    // Target everyone except specific users
    everyoneExcept: (excludeUsers) => ({ 
      everyone: true, 
      exclude_users: Array.isArray(excludeUsers) ? excludeUsers : [excludeUsers] 
    }),

    // Combine multiple rules with AND logic
    and: (...ruleObjects) => {
      const combined = {};
      ruleObjects.forEach(rules => {
        Object.assign(combined, rules);
      });
      return combined;
    },

    // For more complex OR logic, we can extend this later
    // For now, multiple rules in the same object are treated as AND
  };

  /**
   * Get human-readable description of targeting rules
   * @param {Object} tip - The tip object
   * @returns {string} - Human-readable targeting description
   */
  static getTargetingDescription(tip) {
    const descriptions = [];

    // Check specific users
    if (tip.target_user_ids && tip.target_user_ids.length > 0) {
      descriptions.push(`${tip.target_user_ids.length} specific user(s)`);
    }

    // Check targeting rules
    if (tip.target_rules && Object.keys(tip.target_rules).length > 0) {
      const rules = tip.target_rules;

      if (rules.everyone === true) {
        if (rules.exclude_users && rules.exclude_users.length > 0) {
          descriptions.push(`Everyone except ${rules.exclude_users.length} user(s)`);
        } else {
          descriptions.push('Everyone');
        }
      }

      if (rules.roles && rules.roles.length > 0) {
        descriptions.push(`Users with roles: ${rules.roles.join(', ')}`);
      }

      if (rules.permissions && rules.permissions.length > 0) {
        descriptions.push(`Users with permissions: ${rules.permissions.join(', ')}`);
      }

      if (rules.parts && rules.parts.length > 0) {
        descriptions.push(`Parts: ${rules.parts.join(', ')}`);
      }
    }

    return descriptions.length > 0 ? descriptions.join(' + ') : 'No targets specified';
  }

  /**
   * Filter tips for a specific user based on targeting rules
   * @param {Array} tips - Array of tip objects
   * @param {Object} user - The current user object
   * @param {Object} context - Additional context
   * @returns {Array} - Filtered tips that should be visible to the user
   */
  static filterTipsForUser(tips, user, context = {}) {
    if (!tips || !user) return [];

    return tips.filter(tip => this.shouldShowTipToUser(tip, user, context));
  }

  /**
   * Filter tips for general display (excludes creator's own tips unless they're also targets)
   * @param {Array} tips - Array of tip objects  
   * @param {Object} user - The current user object
   * @param {Object} context - Additional context
   * @returns {Array} - Filtered array of tips
   */
  static filterTipsForGeneralDisplay(tips, user, context = {}) {
    if (!tips || !user) return [];

    return tips.filter(tip => this.shouldShowTipToUserExcludingCreator(tip, user, context));
  }

  /**
   * Check if tip should be shown to user (excluding creator privilege)
   * Used for general display areas where creators shouldn't see their own tips unless targeted
   */
  static shouldShowTipToUserExcludingCreator(tip, user, context = {}) {
    if (!tip || !user) return false;

    // console.log('TipTargeting: Evaluating tip for general display', {
    //   tipId: tip.id,
    //   tipContent: tip.content?.substring(0, 50) + '...',
    //   userId: user.id,
    //   creatorUserId: tip.creator_user_id,
    //   targetUserIds: tip.target_user_ids,
    //   targetRules: tip.target_rules
    // });

    // Do NOT show tips just because user is the creator
    // Only show if user is specifically targeted

    // Check specific user targeting
    if (tip.target_user_ids && tip.target_user_ids.includes(user.id)) {
      // console.log('TipTargeting: Showing tip because user is in target_user_ids');
      return true;
    }

    // Check flexible targeting rules
    if (tip.target_rules && Object.keys(tip.target_rules).length > 0) {
      const rulesResult = this.evaluateTargetingRules(tip.target_rules, user, context);
      // console.log('TipTargeting: Rules evaluation result:', rulesResult);
      return rulesResult;
    }

    // console.log('TipTargeting: Not showing tip in general display - user not targeted');
    return false;
  }

  /**
   * Validate targeting rules object
   * @param {Object} rules - The targeting rules to validate
   * @returns {Object} - { valid: boolean, errors: string[] }
   */
  static validateTargetingRules(rules) {
    const errors = [];

    if (!rules || typeof rules !== 'object') {
      return { valid: true, errors: [] }; // Empty rules are valid
    }

    // Validate everyone rule
    if (rules.everyone !== undefined && typeof rules.everyone !== 'boolean') {
      errors.push('everyone rule must be a boolean');
    }

    // Validate roles rule
    if (rules.roles !== undefined) {
      if (!Array.isArray(rules.roles)) {
        errors.push('roles rule must be an array');
      } else if (rules.roles.some(role => typeof role !== 'string')) {
        errors.push('all roles must be strings');
      }
    }

    // Validate permissions rule
    if (rules.permissions !== undefined) {
      if (!Array.isArray(rules.permissions)) {
        errors.push('permissions rule must be an array');
      } else if (rules.permissions.some(perm => typeof perm !== 'string')) {
        errors.push('all permissions must be strings');
      }
    }

    // Validate parts rule
    if (rules.parts !== undefined) {
      if (!Array.isArray(rules.parts)) {
        errors.push('parts rule must be an array');
      } else if (rules.parts.some(part => typeof part !== 'string')) {
        errors.push('all parts must be strings');
      }
    }

    // Validate exclude_users rule
    if (rules.exclude_users !== undefined) {
      if (!Array.isArray(rules.exclude_users)) {
        errors.push('exclude_users rule must be an array');
      } else if (rules.exclude_users.some(userId => typeof userId !== 'string')) {
        errors.push('all excluded user IDs must be strings');
      }
    }

    return { valid: errors.length === 0, errors };
  }
}

// Export for convenience
export const {
  shouldShowTipToUser,
  filterTipsForUser,
  filterTipsForGeneralDisplay,
  createTargetingRules,
  getTargetingDescription,
  validateTargetingRules
} = TipTargeting;